<!-- ./components/AbilityComponent.vue -->
<template>
  <div class="p-4">
    <div class="row">
      <!-- Cards sidebar on md+ screens -->
      <aside v-if="selectedDeck" class="col-3 d-none d-md-block border-end card-list-aside">
        <!-- Card detail preview -->
        <div class="mb-4" v-if="extendedCard">
          <CardDetailComponent :card="extendedCard" />
        </div>

        <div v-if="selectedCard" class="d-flex align-items-center mb-2">
          <span class="me-2">Text Size:</span>
          <button class="btn btn-sm btn-secondary me-1" @click.stop="decreaseCardSize">−</button>
          <span class="mx-2">{{ selectedCard.cardSize.toFixed(1) }} mm</span>
          <button class="btn btn-sm btn-secondary ms-1" @click.stop="increaseCardSize">+</button>
        </div>

        <!-- Card list header with "Add Card" -->
        <div class="d-flex align-items-center mb-2">
          <h3 class="mb-0">
            Card List ({{ totalCount }})
          </h3>
          <button class="btn btn-sm btn-primary ms-2" @click="newCard" title="Add Card">
            <i class="bi bi-plus-lg"></i>
          </button>
        </div>

        <!-- Card tiles -->
        <div v-for="(card, idx) in selectedDeck.cards" :key="idx" class="card mb-2" @click="selectCard(card)"
          :class="{ 'border-primary': selectedCard === card }" style="cursor: pointer;">
          <div class="card-body d-flex align-items-center justify-content-between p-2">
            <div class="flex-grow-1">
              <template v-if="selectedCard === card">
                <input v-model="selectedCard.name" @click.stop class="form-control form-control-sm" />
              </template>
              <template v-else>
                {{ card.name }}
              </template>
            </div>
            <div class="d-flex">
              <!-- Recycle -->
              <button class="btn btn-sm btn-secondary me-1" title="Recycle Card" @click.stop="recycleCard(card)">
                <i class="bi bi-arrow-repeat"></i>
              </button>
              <!-- Hard Color toggle -->
              <button class="btn btn-sm" :class="card.hardColor ? 'btn-success me-1' : 'btn-secondary me-1'"
                @click.stop="toggleCardHardColor(card)" title="Toggle Hard Color">
                <i class="bi bi-droplet-half"></i>
              </button>
              <button class="btn btn-sm" :class="['me-1', card.count === 1 ? 'btn-danger' : 'btn-secondary']"
                @click.stop="decreaseCount(card)">
                − {{ card.count }}
              </button>
              <button class="btn btn-sm btn-secondary" :disabled="card.count >= 4" @click.stop="increaseCount(card)">
                +
              </button>

            </div>
          </div>
        </div>
      </aside>

      <!-- Main content -->
      <section :class="selectedDeck ? 'col-12 col-md-9' : 'col-12'">
        <h2>
          Abilities
          <span v-if="selectedDeck">for “{{ selectedDeck.name }}”</span>
        </h2>

        <!-- Admin action buttons -->
        <div class="mb-3" v-if="isAdmin">
          <button class="btn btn-sm btn-primary me-2" @click="newAbilityClicked">
            New Ability
          </button>
          <button class="btn btn-sm btn-secondary" @click="editAbilityClicked" :disabled="!selectedAbility">
            Edit Ability
          </button>
          <button class="btn btn-sm btn-success ms-2" @click="download">
            Download Abilities
          </button>
          <button class="btn btn-sm btn-warning ms-2" @click="downloadKeywords">
            Download Keywords
          </button>
          <button class="btn btn-sm btn-info ms-2" @click="editKeywordsClicked">
            Edit Keywords
          </button>
        </div>

        <!-- Keyword editor -->
        <div v-if="showKeywords" class="mb-4">
          <h3>Keyword Editor</h3>
          <button class="btn btn-sm btn-primary mb-2" v-if="isAdmin" @click="addKeyword">
            Add Keyword
          </button>
          <div class="border p-2" style="max-height:300px; overflow-y:auto;">
            <div v-for="(kw, idx) in keywords" :key="idx" class="mb-3">
              <div class="row g-2 align-items-start">
                <div class="col-6">
                  <input v-model="kw.name" placeholder="Name" class="form-control mb-2" :disabled="!isAdmin" />
                  <input v-model="kw.tag" placeholder="Tag" class="form-control" :disabled="!isAdmin" />
                </div>
                <div class="col-6">
                  <textarea v-model="kw.detail" placeholder="Detail" class="form-control" rows="3"
                    :disabled="!isAdmin"></textarea>
                </div>
              </div>
              <button class="btn btn-sm btn-danger mt-1" v-if="isAdmin" @click="deleteKeyword(idx)">
                Delete
              </button>
            </div>
            <div v-if="!keywords.length" class="text-muted text-center">
              No keywords defined.
            </div>
          </div>
          <button class="btn btn-sm btn-secondary mt-2" @click="closeKeywords">
            Close Keywords
          </button>
        </div>

        <!-- Abilities table -->
        <div v-else>
          <table class="table table-striped">
            <thead>
              <tr>
                <th>Cost</th>
                <th>Element</th>
                <th>Card Type</th>
                <th>Multiple</th>
                <th>Description</th>
                <th>Atk</th>
                <th>Def</th>
                <th>Speed</th>
                <th v-if="selectedDeck">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ability in abilities" :key="ability.id" @click="isAdmin && selectAbility(ability)"
                :class="{ 'table-active': isAdmin && selectedAbility === ability }"
                :style="{ cursor: isAdmin ? 'pointer' : 'default' }">
                <td>{{ ability.cost }}</td>
                <td>{{ elementMap[ability.element] }}</td>
                <td>{{ ability.cardType }}</td>
                <td>
                  <input type="checkbox" disabled :checked="ability.multiple" />
                </td>
                <td v-html="formatAbilityText(ability.description)"></td>
                <td>{{ ability.bonusAttack }}</td>
                <td>{{ ability.bonusDefense }}</td>
                <td>{{ ability.bonusSpeed }}</td>
                <td v-if="selectedDeck">
                  <button class="btn btn-sm" :class="canRemove(ability) ? 'btn-secondary' : 'btn-outline-secondary'"
                    @click.stop="removeAbilityFromCard(selectedCard, ability.id)" :disabled="!canRemove(ability)">
                    −
                  </button>
                  <button class="btn btn-sm" :class="canAdd(ability) ? 'btn-secondary' : 'btn-outline-secondary'"
                    @click.stop="addAbilityToCard(selectedCard, ability.id, ability.multiple)"
                    :disabled="!canAdd(ability)">
                    +
                  </button>
                </td>
              </tr>
              <tr v-if="!abilities.length">
                <td colspan="9" class="text-center text-muted">
                  No abilities defined yet.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Edit/Create Modal -->
        <div class="modal fade show" tabindex="-1" v-if="showDialog" style="display: block;">
          <div class="modal-dialog">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">
                  {{ isNew ? 'New Ability' : 'Edit Ability' }}
                </h5>
                <button v-if="!isNew && isAdmin" type="button" class="btn btn-sm btn-danger me-2"
                  @click="deleteAbility">
                  Delete
                </button>
                <button type="button" class="btn-close" @click="closeDialog" />
              </div>
              <div class="modal-body">
                <div class="mb-2">
                  <label class="form-label">ID</label>
                  <input v-model="selectedAbility.id" type="text" class="form-control" maxlength="8"
                    :disabled="!isAdmin" />
                </div>
                <div class="mb-2">
                  <label class="form-label">Element</label>
                  <select v-model="selectedAbility.element" class="form-select" :disabled="!isAdmin">
                    <option v-for="opt in elementOptions" :key="opt.code" :value="opt.code">
                      {{ opt.name }}
                    </option>
                  </select>
                </div>
                <div class="mb-2">
                  <label class="form-label">Card Type</label>
                  <select v-model="selectedAbility.cardType" class="form-select" :disabled="!isAdmin">
                    <option v-for="type in cardTypeOptions" :key="type" :value="type">
                      {{ type }}
                    </option>
                  </select>
                </div>
                <div class="form-check mb-3">
                  <input class="form-check-input" type="checkbox" id="multipleCheck" v-model="selectedAbility.multiple"
                    :disabled="!isAdmin" />
                  <label class="form-check-label" for="multipleCheck">Multiple</label>
                </div>
                <div class="mb-2">
                  <label class="form-label">Cost</label>
                  <input v-model="selectedAbility.cost" type="text" class="form-control" :disabled="!isAdmin" />
                </div>
                <div class="mb-2">
                  <label class="form-label">Description</label>
                  <textarea v-model="selectedAbility.description" class="form-control" :disabled="!isAdmin"></textarea>
                </div>
                <div class="mb-2">
                  <label class="form-label">Details</label>
                  <input v-model="selectedAbility.details" type="text" class="form-control" :disabled="!isAdmin" />
                </div>
                <div class="row">
                  <div class="col-4 mb-2">
                    <label class="form-label">Atk</label>
                    <input v-model.number="selectedAbility.bonusAttack" type="number" class="form-control"
                      :disabled="!isAdmin" />
                  </div>
                  <div class="col-4 mb-2">
                    <label class="form-label">Def</label>
                    <input v-model.number="selectedAbility.bonusDefense" type="number" class="form-control"
                      :disabled="!isAdmin" />
                  </div>
                  <div class="col-4 mb-2">
                    <label class="form-label">Speed</label>
                    <input v-model.number="selectedAbility.bonusSpeed" type="number" class="form-control"
                      :disabled="!isAdmin" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-backdrop fade show"></div>
        </div>
      </section>
    </div>

    <!-- Mobile-only cards modal trigger -->
    <button class="btn btn-primary position-fixed d-md-none" style="bottom: 1rem; left: 1rem; z-index: 1050;"
      @click="showCardsModal = true">
      ☰ Cards
    </button>

    <!-- Mobile cards modal -->
    <div v-if="showCardsModal" class="modal fade show" style="display: block;">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Card List</h5>
            <button class="btn btn-sm btn-primary ms-2" @click="newCard" title="Add Card">
              <i class="bi bi-plus-lg"></i>
            </button>
            <button type="button" class="btn-close" @click="showCardsModal = false" />
          </div>
          <div class="modal-body">
            <div v-for="(card, idx) in selectedDeck.cards" :key="idx" class="card mb-2" @click="selectCard(card)"
              :class="{ 'border-primary': selectedCard === card }" style="cursor: pointer;">
              <div class="card-body d-flex align-items-center justify-content-between p-2">
                <div class="flex-grow-1">
                  <template v-if="selectedCard === card">
                    <input v-model="selectedCard.name" @click.stop class="form-control form-control-sm" />
                  </template>
                  <template v-else>
                    {{ card.name }}
                  </template>
                </div>
                <div class="d-flex">
                  <button class="btn btn-sm btn-secondary me-1" title="Recycle Card" @click.stop="recycleCard(card)">
                    <i class="bi bi-arrow-repeat"></i>
                  </button>
                  <button class="btn btn-sm btn-secondary" @click.stop="deleteCard(card)" title="Delete Card">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showCardsModal = false">
              Close
            </button>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show"></div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, inject, watch, computed } from "vue";
import { AbilityService } from "../services/AbilityService";
import { GetCardForView, GetCardForSave } from "../services/CardAdapterService";
import { getRandomCardName, getRandomImageName } from "../services/CardDisplayService";
import CardDetailComponent from "./CardDetailComponent.vue";

function escapeHtml(str = "") {
  return str
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function generateId(n = 8) {
  return Math.random().toString(36).substring(2, 2 + n);
}

export default {
  name: "AbilityComponent",
  components: { CardDetailComponent },
  setup() {
    const isAdmin = inject("isAdmin", false);
    const selectedDeck = inject("selectedDeck", null);
    const saveDeckBox = inject("saveDeckBox");

    const abilities = ref([]);
    const keywords = ref([]);
    const selectedAbility = ref(null);
    const selectedCard = ref(null);
    const extendedCard = ref(null);
    const showDialog = ref(false);
    const showKeywords = ref(false);
    const isNew = ref(false);
    const showCardsModal = ref(false);

    const elementMap = {
      A: "Abyssal",
      R: "Arcane",
      L: "Light",
      E: "Terra",
      X: "Neutral",
    };
    const elementOptions = Object.entries(elementMap).map(([code, name]) => ({
      code,
      name,
    }));
    const cardTypeOptions = ["Fighter", "Ritual", "All"];

    onMounted(async () => {
      const loaded = await AbilityService.loadAbilities();
      abilities.value = loaded.map((a) => ({
        id: a.id,
        element: a.element ?? "T",
        cardType: a.cardType ?? "Fighter",
        cost: a.cost ?? "",
        description: a.description ?? "",
        bonusAttack: a.bonusAttack ?? 0,
        bonusDefense: a.bonusDefense ?? 0,
        bonusSpeed: a.bonusSpeed ?? 0,
        multiple: a.multiple ?? false,
      }));
      keywords.value = await AbilityService.loadKeywords();
    });

    watch(
      selectedCard,
      async (card) => {
        if (!card) {
          extendedCard.value = null;
          return;
        }
        extendedCard.value = await GetCardForView(card);
        const basic = GetCardForSave(card);
        Object.assign(card, basic);
        saveDeckBox();
      },
      { immediate: true, deep: true }
    );

    // Recycle art & name
    const recycleCard = (card) => {
      card.artName = getRandomImageName();
      card.name = getRandomCardName();
      const basic = GetCardForSave(card);
      Object.assign(card, basic);
      saveDeckBox();
    };

    // Toggle hard color flag
    const toggleCardHardColor = (card) => {
      card.hardColor = !card.hardColor;
      if (card !== selectedCard.value) {
        const basic = GetCardForSave(card);
        Object.assign(card, basic);
        saveDeckBox();
      }
    };

    // Format ability description with symbols, TF only once, keywords
    const formatAbilityText = (text = "") => {
      let seenTF = false;
      return text
        .replace(/\n/g, "<br>")
        .replace(/\[([^\]]+)\]/gi, (match, raw) => {
          const tag = raw.toLowerCase();
          if (tag === "tf") {
            if (!seenTF) {
              seenTF = true;
              return "Target Fighter";
            }
            return "that fighter";
          }
          if (tag === "tp") {
            if (!seenTF) {
              seenTF = true;
              return "Target Player";
            }
            return "that player";
          }
          if (tag === "tc") {
            if (!seenTF) {
              seenTF = true;
              return "Target Card";
            }
            return "that card";
          }
          if (/^[ARLE]$/i.test(raw)) {
            const name = elementMap[raw.toUpperCase()];
            const imgSrc = name
              ? `/symbol/Symbol${name}.png`
              : `/symbol/SymbolNeutral.png`;
            return `<img src="${imgSrc}" alt="${raw}" class="rules-cost-image" />`;
          }
          if (/^-?\d+$/.test(raw)) {
            return `<img src="/symbol/Symbol${raw}.png" alt="${raw}" class="rules-cost-image" />`;
          }
          if (raw === "x") {
            return `<img src="/symbol/Symbolx.png" alt="x" class="rules-cost-image" />`;
          }
          if (raw === "X") {
            return `<strong>X</strong>`;
          }
          const kw = keywords.value.find((k) => k.tag === tag);
          if (kw) {
            const tt = kw.detail ? ` title="${escapeHtml(kw.detail)}"` : "";
            return `<span class="keyword"${tt}>${kw.name}</span>`;
          }
          return `<strong>${raw.toUpperCase()}</strong>`;
        });
    };

    // Create a new card
    const newCard = () => {
      if (!selectedDeck.value) return;
      const newC = {
        name: "New Card",
        abilities: [],
        count: 1,
        hardColor: false,
        cardSize: 2.3,
        artName: ""
      };
      selectedDeck.value.cards.push(newC);
      saveDeckBox();
      selectedCard.value = newC;
    };

    const selectCard = (card) => (selectedCard.value = card);

    // Delete card outright
    const deleteCard = (card) => {
      const i = selectedDeck.value.cards.indexOf(card);
      if (i !== -1) {
        selectedDeck.value.cards.splice(i, 1);
        if (selectedCard.value === card) selectedCard.value = null;
        saveDeckBox();
      }
    };

    // Count helpers
    const increaseCount = (card) => {
      if (card.count < 4) {
        card.count++;
        saveDeckBox();
      }
    };

    const decreaseCount = (card) => {
      if (card.count > 1) {
        card.count--;
      } else {
        // remove card when count would drop below 1
        const idx = selectedDeck.value.cards.indexOf(card);
        if (idx !== -1) {
          selectedDeck.value.cards.splice(idx, 1);
          if (selectedCard.value === card) selectedCard.value = null;
        }
      }
      saveDeckBox();
    };

    // Ability add/remove
    const cardHasAbility = (card, guid) =>
      Array.isArray(card?.abilities) && card.abilities.includes(guid);
    const canRemove = (ability) =>
      selectedCard.value && cardHasAbility(selectedCard.value, ability.id);
    const canAdd = (ability) =>
      selectedCard.value &&
      (ability.multiple || !cardHasAbility(selectedCard.value, ability.id));

    const addAbilityToCard = (card, guid, multiple) => {
      if (!card) return;
      card.abilities = card.abilities || [];
      if (!multiple && card.abilities.includes(guid)) return;
      card.abilities.push(guid);
    };
    const removeAbilityFromCard = (card, guid) => {
      if (!card?.abilities) return;
      const idx = card.abilities.indexOf(guid);
      if (idx !== -1) card.abilities.splice(idx, 1);
    };

    // Card size adjust
    const increaseCardSize = () => {
      if (!selectedCard.value) return;
      selectedCard.value.cardSize = +(selectedCard.value.cardSize + 0.1).toFixed(1);
    };
    const decreaseCardSize = () => {
      if (!selectedCard.value) return;
      selectedCard.value.cardSize = Math.max(
        1,
        +(selectedCard.value.cardSize - 0.1).toFixed(1)
      );
    };

    // Ability editor controls
    const selectAbility = (a) => {
      if (isAdmin) selectedAbility.value = a;
    };
    const newAbilityClicked = () => {
      selectedAbility.value = {
        id: generateId(8),
        element: "T",
        cardType: "Fighter",
        cost: "",
        description: "",
        details: "",
        bonusAttack: 0,
        bonusDefense: 0,
        bonusSpeed: 0,
        multiple: false,
      };
      isNew.value = true;
      showDialog.value = true;
    };
    const editAbilityClicked = () => {
      if (!selectedAbility.value) return;
      isNew.value = false;
      showDialog.value = true;
    };
    const closeDialog = () => {
      if (isNew.value && selectedAbility.value) {
        abilities.value.push({ ...selectedAbility.value });
      }
      showDialog.value = false;
    };
    const deleteAbility = () => {
      if (!selectedAbility.value) return;
      abilities.value = abilities.value.filter(
        (ab) => ab.id !== selectedAbility.value.id
      );
      selectedAbility.value = null;
      showDialog.value = false;
    };
    const download = () => AbilityService.downloadJSON(abilities.value);

    // Keyword editor
    const editKeywordsClicked = () => (showKeywords.value = true);
    const closeKeywords = () => (showKeywords.value = false);
    const addKeyword = () => keywords.value.push({ name: "", tag: "", detail: "" });
    const deleteKeyword = (idx) => keywords.value.splice(idx, 1);
    const downloadKeywords = () => AbilityService.downloadKeywordsJSON(keywords.value);

    const totalCount = computed(() => {
      if (!selectedDeck.value) return 0;
      return selectedDeck.value.cards
        .reduce((sum, card) => sum + (card.count || 0), 0);
    });

    return {
      isAdmin,
      selectedDeck,
      abilities,
      keywords,
      selectedAbility,
      selectedCard,
      extendedCard,
      showDialog,
      showKeywords,
      isNew,
      showCardsModal,
      elementMap,
      elementOptions,
      cardTypeOptions,
      formatAbilityText,
      selectAbility,
      newAbilityClicked,
      editAbilityClicked,
      closeDialog,
      deleteAbility,
      download,
      editKeywordsClicked,
      closeKeywords,
      addKeyword,
      deleteKeyword,
      downloadKeywords,
      newCard,
      selectCard,
      deleteCard,
      increaseCount,
      decreaseCount,
      cardHasAbility,
      canRemove,
      canAdd,
      addAbilityToCard,
      removeAbilityFromCard,
      recycleCard,
      toggleCardHardColor,
      increaseCardSize,
      decreaseCardSize,
      totalCount,
    };
  },
};
</script>


<style scoped>
.card-list-aside {
  position: sticky;
  top: 110px;
  /* matches header height */
  max-height: calc(100vh - 110px);
  overflow-y: auto;
}
</style>
